/**
 * IMPORTANT NOTE FOR PRODUCTION:
 * ================================
 * When production is enabled, the backend API will return pre-aggregated data.
 * Frontend will NOT need to perform any aggregation calculations.
 *
 * Current Implementation (Testing Only):
 * - Provides 45 months of raw test data (2022-01 to 2025-09)
 * - Frontend dynamically aggregates data based on user-selected period and date range
 * - This is for testing environment only
 *
 * Production Environment:
 * - Backend API accepts: period, start_date, end_date, completed_only
 * - Backend returns aggregated time_series and summary data
 * - Frontend only displays data, no calculations needed
 *
 * When switching to production:
 * 1. Set USE_TEST_DATA to false in Dashboard/index.js
 * 2. Verify API endpoints are correct
 * 3. Remove or comment out test data imports
 */

// Test data for Dashboard Overview API responses
// Based on different time periods: monthly, quarterly, weekly, yearly

export const monthlyTestData = {
  current_period: {
    total_income: 11000.0,
    total_jobs: 20,
    avg_income: 550.0,
    period_start: "2024-10-01",
    period_end: "2024-10-31",
  },
  previous_period: {
    total_income: 10416.0,
    total_jobs: 19,
    avg_income: 514.0,
    period_start: "2024-09-01",
    period_end: "2024-09-30",
  },
  comparison: {
    income_change_percent: 5.6,
    jobs_change_percent: 5.3,
    avg_income_change_percent: 7.1,
  },
  top_metrics: {
    top_agent_income: {
      agents: [
        {
          agent: {
            id: "uuid1",
            name: "Jim Moriarty",
            role: "Primary Inspector",
          },
          total_income: 7000.0,
          total_jobs: 16,
          avg_income: 437.5,
          income_percentage: 63.7,
          jobs_percentage: 80.0,
        },
        {
          agent: {
            id: "uuid2",
            name: "Christopher Montgomery",
            role: "Primary Inspector",
          },
          total_income: 2000.0,
          total_jobs: 3,
          avg_income: 666.67,
          income_percentage: 18.2,
          jobs_percentage: 15.0,
        },
      ],
      others: {
        total_income: 2000.0,
        total_jobs: 1,
        income_percentage: 18.1,
        jobs_percentage: 5.0,
      },
    },
    top_region: {
      regions: [
        {
          location: "Seattle, WA",
          city: "Seattle",
          state: "WA",
          total_income: 3700.0,
          total_jobs: 8,
          avg_income: 462.5,
          percentage: 33.6,
        },
        {
          location: "Redmond, WA",
          city: "Redmond",
          state: "WA",
          total_income: 2800.0,
          total_jobs: 5,
          avg_income: 560.0,
          percentage: 25.5,
        },
        {
          location: "Bellevue, WA",
          city: "Bellevue",
          state: "WA",
          total_income: 2200.0,
          total_jobs: 4,
          avg_income: 550.0,
          percentage: 20.0,
        },
      ],
      others: {
        total_income: 2300.0,
        total_jobs: 3,
        percentage: 20.9,
      },
    },
    top_quote_range: {
      quote_ranges: [
        {
          range: "$500-$599",
          min_value: 500.0,
          max_value: 599.99,
          job_count: 16,
          total_income: 8800.0,
          percentage: 80.0,
        },
        {
          range: "$300-$399",
          min_value: 300.0,
          max_value: 399.99,
          job_count: 3,
          total_income: 1050.0,
          percentage: 15.0,
        },
        {
          range: "$1000+",
          min_value: 1000.0,
          max_value: null,
          job_count: 1,
          total_income: 1150.0,
          percentage: 5.0,
        },
      ],
      others: null,
    },
    top_property_type: {
      property_types: [
        {
          type: "Single Family Home",
          job_count: 12,
          total_income: 6600.0,
          percentage: 60.0,
        },
        {
          type: "Condo/Townhouse",
          job_count: 4,
          total_income: 2200.0,
          percentage: 20.0,
        },
        {
          type: "Property Type not specified",
          job_count: 4,
          total_income: 2200.0,
          percentage: 20.0,
        },
      ],
      others: null,
    },
  },
};

export const quarterlyTestData = {
  current_period: {
    total_income: 32500.0,
    total_jobs: 58,
    avg_income: 560.34,
    period_start: "2024-10-01",
    period_end: "2024-12-31",
  },
  previous_period: {
    total_income: 28900.0,
    total_jobs: 52,
    avg_income: 555.77,
    period_start: "2024-07-01",
    period_end: "2024-09-30",
  },
  comparison: {
    income_change_percent: 12.5,
    jobs_change_percent: 11.5,
    avg_income_change_percent: 0.8,
  },
  top_metrics: {
    top_agent_income: {
      agents: [
        {
          agent: {
            id: "uuid1",
            name: "Jim Moriarty",
            role: "Primary Inspector",
          },
          total_income: 20500.0,
          total_jobs: 38,
          avg_income: 539.47,
          income_percentage: 63.1,
          jobs_percentage: 65.5,
        },
        {
          agent: {
            id: "uuid2",
            name: "Christopher Montgomery",
            role: "Primary Inspector",
          },
          total_income: 7200.0,
          total_jobs: 12,
          avg_income: 600.0,
          income_percentage: 22.2,
          jobs_percentage: 20.7,
        },
      ],
      others: {
        total_income: 4800.0,
        total_jobs: 8,
        income_percentage: 14.7,
        jobs_percentage: 13.8,
      },
    },
    top_region: {
      regions: [
        {
          location: "Seattle, WA",
          city: "Seattle",
          state: "WA",
          total_income: 12200.0,
          total_jobs: 22,
          avg_income: 554.55,
          percentage: 37.5,
        },
        {
          location: "Redmond, WA",
          city: "Redmond",
          state: "WA",
          total_income: 8500.0,
          total_jobs: 15,
          avg_income: 566.67,
          percentage: 26.2,
        },
        {
          location: "Bellevue, WA",
          city: "Bellevue",
          state: "WA",
          total_income: 6800.0,
          total_jobs: 12,
          avg_income: 566.67,
          percentage: 20.9,
        },
      ],
      others: {
        total_income: 5000.0,
        total_jobs: 9,
        percentage: 15.4,
      },
    },
    top_quote_range: {
      quote_ranges: [
        {
          range: "$500-$599",
          min_value: 500.0,
          max_value: 599.99,
          job_count: 44,
          total_income: 24200.0,
          percentage: 75.9,
        },
        {
          range: "$300-$399",
          min_value: 300.0,
          max_value: 399.99,
          job_count: 9,
          total_income: 3150.0,
          percentage: 15.5,
        },
        {
          range: "$1000+",
          min_value: 1000.0,
          max_value: null,
          job_count: 5,
          total_income: 5150.0,
          percentage: 8.6,
        },
      ],
      others: null,
    },
    top_property_type: {
      property_types: [
        {
          type: "Single Family Home",
          job_count: 35,
          total_income: 19500.0,
          percentage: 60.3,
        },
        {
          type: "Condo/Townhouse",
          job_count: 12,
          total_income: 7200.0,
          percentage: 20.7,
        },
        {
          type: "Property Type not specified",
          job_count: 11,
          total_income: 5800.0,
          percentage: 19.0,
        },
      ],
      others: null,
    },
  },
};

export const weeklyTestData = {
  current_period: {
    total_income: 2750.0,
    total_jobs: 5,
    avg_income: 550.0,
    period_start: "2024-10-07",
    period_end: "2024-10-13",
  },
  previous_period: {
    total_income: 2200.0,
    total_jobs: 4,
    avg_income: 550.0,
    period_start: "2024-09-30",
    period_end: "2024-10-06",
  },
  comparison: {
    income_change_percent: 25.0,
    jobs_change_percent: 25.0,
    avg_income_change_percent: 0.0,
  },
  top_metrics: {
    top_agent_income: {
      agents: [
        {
          agent: {
            id: "uuid1",
            name: "Jim Moriarty",
            role: "Primary Inspector",
          },
          total_income: 1650.0,
          total_jobs: 3,
          avg_income: 550.0,
          income_percentage: 60.0,
          jobs_percentage: 60.0,
        },
        {
          agent: {
            id: "uuid2",
            name: "Christopher Montgomery",
            role: "Primary Inspector",
          },
          total_income: 1100.0,
          total_jobs: 2,
          avg_income: 550.0,
          income_percentage: 40.0,
          jobs_percentage: 40.0,
        },
      ],
      others: {
        total_income: 0.0,
        total_jobs: 0,
        income_percentage: 0.0,
        jobs_percentage: 0.0,
      },
    },
    top_region: {
      regions: [
        {
          location: "Seattle, WA",
          city: "Seattle",
          state: "WA",
          total_income: 1650.0,
          total_jobs: 3,
          avg_income: 550.0,
          percentage: 60.0,
        },
        {
          location: "Redmond, WA",
          city: "Redmond",
          state: "WA",
          total_income: 1100.0,
          total_jobs: 2,
          avg_income: 550.0,
          percentage: 40.0,
        },
      ],
      others: {
        total_income: 0.0,
        total_jobs: 0,
        percentage: 0.0,
      },
    },
    top_quote_range: {
      quote_ranges: [
        {
          range: "$500-$599",
          min_value: 500.0,
          max_value: 599.99,
          job_count: 5,
          total_income: 2750.0,
          percentage: 100.0,
        },
      ],
      others: null,
    },
    top_property_type: {
      property_types: [
        {
          type: "Single Family Home",
          job_count: 3,
          total_income: 1650.0,
          percentage: 60.0,
        },
        {
          type: "Condo/Townhouse",
          job_count: 2,
          total_income: 1100.0,
          percentage: 40.0,
        },
      ],
      others: null,
    },
  },
};

export const yearlyTestData = {
  current_period: {
    total_income: 145000.0,
    total_jobs: 260,
    avg_income: 557.69,
    period_start: "2024-01-01",
    period_end: "2024-12-31",
  },
  previous_period: {
    total_income: 128500.0,
    total_jobs: 235,
    avg_income: 546.81,
    period_start: "2023-01-01",
    period_end: "2023-12-31",
  },
  comparison: {
    income_change_percent: 12.8,
    jobs_change_percent: 10.6,
    avg_income_change_percent: 2.0,
  },
  top_metrics: {
    top_agent_income: {
      agents: [
        {
          agent: {
            id: "uuid1",
            name: "Jim Moriarty",
            role: "Primary Inspector",
          },
          total_income: 89000.0,
          total_jobs: 160,
          avg_income: 556.25,
          income_percentage: 61.4,
          jobs_percentage: 61.5,
        },
        {
          agent: {
            id: "uuid2",
            name: "Christopher Montgomery",
            role: "Primary Inspector",
          },
          total_income: 32000.0,
          total_jobs: 58,
          avg_income: 551.72,
          income_percentage: 22.1,
          jobs_percentage: 22.3,
        },
        {
          agent: {
            id: "uuid3",
            name: "Sarah Wilson",
            role: "Inspector",
          },
          total_income: 24000.0,
          total_jobs: 42,
          avg_income: 571.43,
          income_percentage: 16.5,
          jobs_percentage: 16.2,
        },
      ],
      others: {
        total_income: 0.0,
        total_jobs: 0,
        income_percentage: 0.0,
        jobs_percentage: 0.0,
      },
    },
    top_region: {
      regions: [
        {
          location: "Seattle, WA",
          city: "Seattle",
          state: "WA",
          total_income: 52000.0,
          total_jobs: 94,
          avg_income: 553.19,
          percentage: 35.9,
        },
        {
          location: "Redmond, WA",
          city: "Redmond",
          state: "WA",
          total_income: 38000.0,
          total_jobs: 68,
          avg_income: 558.82,
          percentage: 26.2,
        },
        {
          location: "Bellevue, WA",
          city: "Bellevue",
          state: "WA",
          total_income: 29000.0,
          total_jobs: 52,
          avg_income: 557.69,
          percentage: 20.0,
        },
      ],
      others: {
        total_income: 26000.0,
        total_jobs: 46,
        percentage: 17.9,
      },
    },
    top_quote_range: {
      quote_ranges: [
        {
          range: "$500-$599",
          min_value: 500.0,
          max_value: 599.99,
          job_count: 195,
          total_income: 107250.0,
          percentage: 75.0,
        },
        {
          range: "$300-$399",
          min_value: 300.0,
          max_value: 399.99,
          job_count: 42,
          total_income: 14700.0,
          percentage: 16.2,
        },
        {
          range: "$1000+",
          min_value: 1000.0,
          max_value: null,
          job_count: 23,
          total_income: 23050.0,
          percentage: 8.8,
        },
      ],
      others: null,
    },
    top_property_type: {
      property_types: [
        {
          type: "Single Family Home",
          job_count: 156,
          total_income: 87000.0,
          percentage: 60.0,
        },
        {
          type: "Condo/Townhouse",
          job_count: 52,
          total_income: 29000.0,
          percentage: 20.0,
        },
        {
          type: "Property Type not specified",
          job_count: 52,
          total_income: 29000.0,
          percentage: 20.0,
        },
      ],
      others: null,
    },
  },
};

// Helper function to get test data by period
export const getTestDataByPeriod = (period) => {
  switch (period) {
    case "monthly":
      return monthlyTestData;
    case "quarterly":
      return quarterlyTestData;
    case "weekly":
      return weeklyTestData;
    case "yearly":
      return yearlyTestData;
    default:
      return monthlyTestData;
  }
};

// ===================================================================
// RAW DATA FOR DYNAMIC TREND CALCULATION (45 months: 2022-01 to 2025-09)
// ===================================================================

const generateRawMonthlyData = () => {
  const data = [];
  const startYear = 2022;
  const startMonth = 1;
  const endYear = 2025;
  const endMonth = 9;

  // Base values for realistic variation
  const baseIncome = 10000;
  const baseJobs = 20;

  let currentYear = startYear;
  let currentMonth = startMonth;

  while (
    currentYear < endYear ||
    (currentYear === endYear && currentMonth <= endMonth)
  ) {
    // Seasonal variation (higher in spring/summer)
    const seasonalMultiplier =
      0.8 + 0.4 * Math.sin(((currentMonth - 1) * Math.PI) / 6);

    // Year over year growth (5% annually)
    const yearGrowth = Math.pow(1.05, currentYear - startYear);

    // Random variation (-20% to +20%)
    const randomVariation = 0.8 + Math.random() * 0.4;

    const totalIncome = Math.round(
      baseIncome * seasonalMultiplier * yearGrowth * randomVariation
    );
    const totalJobs = Math.round(
      baseJobs * seasonalMultiplier * yearGrowth * randomVariation
    );
    const completedJobs = Math.round(totalJobs * (0.85 + Math.random() * 0.1)); // 85-95% completion
    const incompleteJobs = totalJobs - completedJobs;
    const avgIncome = Math.round(totalIncome / totalJobs);
    const incompleteIncome = Math.round(incompleteJobs * avgIncome * 0.9); // 90% of average for estimates

    data.push({
      year: currentYear,
      month: currentMonth,
      period: `${currentYear}-${String(currentMonth).padStart(2, "0")}`,
      total_income: totalIncome,
      total_jobs: totalJobs,
      avg_income: avgIncome,
      completed_jobs: completedJobs,
      incomplete_jobs: incompleteJobs,
      incomplete_income: incompleteIncome,
    });

    currentMonth++;
    if (currentMonth > 12) {
      currentMonth = 1;
      currentYear++;
    }
  }

  return data;
};

// Generate the raw data
const rawMonthlyData = generateRawMonthlyData();

// ===================================================================
// DYNAMIC TREND DATA CALCULATION
// ===================================================================

export const getTrendTestData = (period, startDate, endDate) => {
  // Convert startDate and endDate to Date objects for comparison
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Filter raw data based on date range
  const filteredData = rawMonthlyData.filter((item) => {
    const itemDate = new Date(
      `${item.year}-${String(item.month).padStart(2, "0")}-01`
    );
    return itemDate >= start && itemDate <= end;
  });

  if (filteredData.length === 0) {
    return {
      time_series: [],
      summary: {
        total_income: 0,
        total_jobs: 0,
        avg_income: 0,
        period_count: 0,
      },
    };
  }

  let aggregatedData = [];

  switch (period) {
    case "monthly":
      aggregatedData = filteredData.map((item) => ({
        period: item.period,
        period_label: getMonthLabel(item.year, item.month),
        total_income: item.total_income,
        total_jobs: item.total_jobs,
        avg_income: item.avg_income,
        completed_jobs: item.completed_jobs,
        incomplete_jobs: item.incomplete_jobs,
        incomplete_income: item.incomplete_income,
      }));
      break;

    case "quarterly":
      const quarterlyMap = new Map();

      filteredData.forEach((item) => {
        const quarter = Math.floor((item.month - 1) / 3) + 1;
        const quarterKey = `${item.year}-Q${quarter}`;

        if (!quarterlyMap.has(quarterKey)) {
          quarterlyMap.set(quarterKey, {
            period: quarterKey,
            period_label: `Q${quarter} ${item.year}`,
            total_income: 0,
            total_jobs: 0,
            completed_jobs: 0,
            incomplete_jobs: 0,
            incomplete_income: 0,
            year: item.year,
            quarter: quarter,
          });
        }

        const quarterData = quarterlyMap.get(quarterKey);
        quarterData.total_income += item.total_income;
        quarterData.total_jobs += item.total_jobs;
        quarterData.completed_jobs += item.completed_jobs;
        quarterData.incomplete_jobs += item.incomplete_jobs;
        quarterData.incomplete_income += item.incomplete_income;
      });

      aggregatedData = Array.from(quarterlyMap.values())
        .map((item) => ({
          ...item,
          avg_income: Math.round(item.total_income / item.total_jobs),
        }))
        .sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return a.quarter - b.quarter;
        });
      break;

    case "weekly": {
      // Get start and end Sundays for the range
      const startSunday = getSundayOfWeek(start);
      const endSunday = getSundayOfWeek(end);

      // Generate weekly data points from start Sunday to end Sunday
      const weeklyData = [];
      let currentSunday = new Date(startSunday);

      while (currentSunday <= endSunday) {
        const weekStart = new Date(currentSunday);
        const weekEnd = new Date(currentSunday);
        weekEnd.setDate(weekEnd.getDate() + 6); // Saturday

        // Find monthly data that overlaps with this week
        const weekData = {
          period: weekStart.toISOString().split("T")[0],
          period_label: formatWeekDisplay(weekStart),
          total_income: 0,
          total_jobs: 0,
          completed_jobs: 0,
          incomplete_jobs: 0,
          incomplete_income: 0,
        };

        // Aggregate data from months that overlap with this week
        filteredData.forEach((monthItem) => {
          const monthStart = new Date(
            `${monthItem.year}-${String(monthItem.month).padStart(2, "0")}-01`
          );
          const monthEnd = new Date(monthItem.year, monthItem.month, 0); // Last day of month

          // Check if month overlaps with this week
          if (!(monthEnd < weekStart || monthStart > weekEnd)) {
            // Calculate overlap days
            const overlapStart =
              monthStart > weekStart ? monthStart : weekStart;
            const overlapEnd = monthEnd < weekEnd ? monthEnd : weekEnd;
            const overlapDays =
              Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24)) +
              1;
            const monthDays = new Date(
              monthItem.year,
              monthItem.month,
              0
            ).getDate();
            const ratio = overlapDays / monthDays;

            // Add proportional data
            weekData.total_income += Math.round(monthItem.total_income * ratio);
            weekData.total_jobs += Math.round(monthItem.total_jobs * ratio);
            weekData.completed_jobs += Math.round(
              monthItem.completed_jobs * ratio
            );
            weekData.incomplete_jobs += Math.round(
              monthItem.incomplete_jobs * ratio
            );
            weekData.incomplete_income += Math.round(
              monthItem.incomplete_income * ratio
            );
          }
        });

        weekData.avg_income =
          weekData.total_jobs > 0
            ? Math.round(weekData.total_income / weekData.total_jobs)
            : 0;
        weeklyData.push(weekData);

        // Move to next Sunday
        currentSunday.setDate(currentSunday.getDate() + 7);
      }

      aggregatedData = weeklyData;
      break;
    }

    case "yearly":
      const yearlyMap = new Map();

      filteredData.forEach((item) => {
        const yearKey = item.year.toString();

        if (!yearlyMap.has(yearKey)) {
          yearlyMap.set(yearKey, {
            period: yearKey,
            period_label: yearKey,
            total_income: 0,
            total_jobs: 0,
            completed_jobs: 0,
            incomplete_jobs: 0,
            incomplete_income: 0,
            year: item.year,
          });
        }

        const yearData = yearlyMap.get(yearKey);
        yearData.total_income += item.total_income;
        yearData.total_jobs += item.total_jobs;
        yearData.completed_jobs += item.completed_jobs;
        yearData.incomplete_jobs += item.incomplete_jobs;
        yearData.incomplete_income += item.incomplete_income;
      });

      aggregatedData = Array.from(yearlyMap.values())
        .map((item) => ({
          ...item,
          avg_income: Math.round(item.total_income / item.total_jobs),
        }))
        .sort((a, b) => a.year - b.year);
      break;

    default:
      aggregatedData = filteredData;
  }

  // Calculate summary
  const summary = {
    total_income: aggregatedData.reduce(
      (sum, item) => sum + item.total_income,
      0
    ),
    total_jobs: aggregatedData.reduce((sum, item) => sum + item.total_jobs, 0),
    period_count: aggregatedData.length,
  };
  summary.avg_income =
    summary.total_jobs > 0
      ? Math.round(summary.total_income / summary.total_jobs)
      : 0;

  return {
    time_series: aggregatedData,
    summary: summary,
  };
};

// Helper function to format month labels
const getMonthLabel = (year, month) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[month - 1]} ${year}`;
};

// Helper functions for US week standard (Sunday = start of week)
const getSundayOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday, 6 = Saturday
  const sunday = new Date(d);
  sunday.setDate(d.getDate() - day);
  return sunday;
};

const formatWeekDisplay = (date) => {
  const d = new Date(date);
  const monthShort = d.toLocaleDateString("en-US", { month: "short" });
  const day = d.getDate();
  return `${monthShort} ${day}`;
};
